import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import {
  Store, Package, MapPin, ShoppingCart, ArrowLeft, Plus, Trash2, Edit3, Save, X, RefreshCw, Loader2,
} from "lucide-react";
import { useAuth } from "../../lib/auth";
import { grocery, formatXAF } from "../../lib/grocery";
import { toast } from "sonner";
import { formatApiErrorDetail } from "../../lib/api";

const EMPTY_SHOP = { name: "", description: "", category_focus: "general", zone_slug: "", address: "", phone: "", image_url: "", open: true };
const EMPTY_PRODUCT = { shop_id: "", name: "", category: "staples", subtype: "", unit: "unité", price_xaf: 0, image_url: "", description: "", available: true, quick_buy: false };

export default function AdminGroceryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user === false) return;
    if (user === null) return;
    if (user?.role !== "admin") navigate("/");
  }, [user, navigate]);

  if (!user || user === false || user.role !== "admin") {
    return (
      <div className="max-w-2xl mx-auto px-5 py-10">
        <p className="text-brand-inkSoft">Accès réservé aux administrateurs.</p>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] bg-brand-paper pb-16" data-testid="grocery-admin-page">
      <section className="bg-brand-ink text-white">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6">
          <Link to="/admin" className="text-white/70 text-sm flex items-center gap-1 mb-2 hover:text-brand-gold" data-testid="grocery-admin-back">
            <ArrowLeft className="w-4 h-4" /> Admin Kanama
          </Link>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold inline-flex items-center gap-2">
            <Store className="w-6 h-6" /> Kanama Courses · Admin
          </h1>
          <p className="text-sm text-white/70 mt-1">Boutiques · Produits · Zones · Commandes</p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-6">
        <Tabs defaultValue="shops" className="w-full">
          <TabsList className="grid grid-cols-4 w-full sm:w-auto sm:inline-flex" data-testid="grocery-admin-tabs">
            <TabsTrigger value="shops" data-testid="grocery-admin-tab-shops"><Store className="w-4 h-4 mr-1.5" />Boutiques</TabsTrigger>
            <TabsTrigger value="products" data-testid="grocery-admin-tab-products"><Package className="w-4 h-4 mr-1.5" />Produits</TabsTrigger>
            <TabsTrigger value="zones" data-testid="grocery-admin-tab-zones"><MapPin className="w-4 h-4 mr-1.5" />Zones</TabsTrigger>
            <TabsTrigger value="orders" data-testid="grocery-admin-tab-orders"><ShoppingCart className="w-4 h-4 mr-1.5" />Commandes</TabsTrigger>
          </TabsList>

          <TabsContent value="shops" className="mt-5"><ShopsTab /></TabsContent>
          <TabsContent value="products" className="mt-5"><ProductsTab /></TabsContent>
          <TabsContent value="zones" className="mt-5"><ZonesTab /></TabsContent>
          <TabsContent value="orders" className="mt-5"><OrdersTab /></TabsContent>
        </Tabs>
      </section>
    </div>
  );
}

// ===== SHOPS =====
function ShopsTab() {
  const [shops, setShops] = useState([]);
  const [zones, setZones] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | shop object
  const [draft, setDraft] = useState(EMPTY_SHOP);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [s, z] = await Promise.all([grocery.adminShops(), grocery.zones()]);
    setShops(s);
    setZones(z.zones || []);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setDraft(EMPTY_SHOP); setEditing("new"); };
  const startEdit = (shop) => {
    setDraft({
      name: shop.name, description: shop.description || "", category_focus: shop.category_focus || "general",
      zone_slug: shop.zone_slug, address: shop.address || "", phone: shop.phone || "",
      image_url: shop.image_url || "", open: shop.open,
    });
    setEditing(shop);
  };

  const save = async () => {
    if (!draft.name || !draft.zone_slug) { toast.error("Nom et zone requis"); return; }
    setLoading(true);
    try {
      if (editing === "new") {
        await grocery.adminCreateShop(draft);
        toast.success("Boutique créée");
      } else {
        await grocery.adminUpdateShop(editing.id, draft);
        toast.success("Boutique mise à jour");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const del = async (shop) => {
    if (!confirm(`Supprimer "${shop.name}" et tous ses produits ?`)) return;
    try {
      await grocery.adminDeleteShop(shop.id);
      toast.success("Supprimée");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl font-semibold text-brand-ink">{shops.length} boutique{shops.length > 1 ? "s" : ""}</h2>
        <Button onClick={startNew} className="bg-brand-gold hover:bg-brand-goldDark text-brand-black" data-testid="grocery-admin-shop-new">
          <Plus className="w-4 h-4 mr-1.5" /> Nouvelle boutique
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {shops.map((s) => (
          <Card key={s.id} className="p-4 rounded-2xl border-brand-line" data-testid={`grocery-admin-shop-${s.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="font-heading font-semibold text-brand-ink leading-tight">{s.name}</div>
              {s.open ? <Badge className="bg-emerald-600">Ouvert</Badge> : <Badge className="bg-slate-400">Fermé</Badge>}
            </div>
            <div className="text-xs text-brand-inkSoft space-y-0.5 mb-3">
              <div><MapPin className="w-3 h-3 inline -mt-0.5 mr-1" /> {s.zone_slug} · {s.address}</div>
              <div>{s.phone}</div>
              <div className="text-[11px] uppercase tracking-wider">Spécialité : {s.category_focus}</div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => startEdit(s)} className="flex-1" data-testid={`grocery-admin-shop-edit-${s.id}`}>
                <Edit3 className="w-3.5 h-3.5 mr-1" /> Modifier
              </Button>
              <Button size="sm" variant="outline" onClick={() => del(s)} className="text-brand-terracotta border-brand-terracotta/40" data-testid={`grocery-admin-shop-del-${s.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      {editing && (
        <ModalForm title={editing === "new" ? "Nouvelle boutique" : "Modifier boutique"} onClose={() => setEditing(null)} onSave={save} loading={loading} testid="grocery-admin-shop-form">
          <Field label="Nom *" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} testid="shop-form-name" />
          <Field label="Description" value={draft.description} onChange={(v) => setDraft({ ...draft, description: v })} testid="shop-form-desc" />
          <SelectField
            label="Zone *" value={draft.zone_slug} onChange={(v) => setDraft({ ...draft, zone_slug: v })}
            options={zones.map((z) => ({ value: z.slug, label: z.name }))} testid="shop-form-zone"
          />
          <SelectField
            label="Spécialité"
            value={draft.category_focus}
            onChange={(v) => setDraft({ ...draft, category_focus: v })}
            options={[
              { value: "general", label: "Générale" },
              { value: "meat", label: "Boucherie" },
              { value: "fish", label: "Poissonnerie" },
              { value: "produce", label: "Fruits & Légumes" },
              { value: "staples", label: "Riz / Pâtes" },
              { value: "drinks", label: "Boissons" },
              { value: "bakery", label: "Boulangerie" },
              { value: "hygiene", label: "Hygiène" },
            ]}
            testid="shop-form-cat"
          />
          <Field label="Adresse" value={draft.address} onChange={(v) => setDraft({ ...draft, address: v })} testid="shop-form-address" />
          <Field label="Téléphone" value={draft.phone} onChange={(v) => setDraft({ ...draft, phone: v })} testid="shop-form-phone" />
          <Field label="Image URL" value={draft.image_url} onChange={(v) => setDraft({ ...draft, image_url: v })} testid="shop-form-image" />
          <div className="flex items-center justify-between">
            <Label className="text-sm">Ouvert</Label>
            <Switch checked={draft.open} onCheckedChange={(v) => setDraft({ ...draft, open: v })} data-testid="shop-form-open" />
          </div>
        </ModalForm>
      )}
    </div>
  );
}

// ===== PRODUCTS =====
function ProductsTab() {
  const [shops, setShops] = useState([]);
  const [products, setProducts] = useState([]);
  const [shopId, setShopId] = useState("");
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(EMPTY_PRODUCT);
  const [loading, setLoading] = useState(false);

  useEffect(() => { grocery.adminShops().then((s) => { setShops(s); if (!shopId && s[0]) setShopId(s[0].id); }); }, []);
  useEffect(() => {
    if (!shopId) return;
    grocery.products({ shop_id: shopId, limit: 500 }).then(setProducts);
  }, [shopId]);

  const startNew = () => { setDraft({ ...EMPTY_PRODUCT, shop_id: shopId }); setEditing("new"); };
  const startEdit = (p) => {
    setDraft({
      shop_id: p.shop_id, name: p.name, category: p.category, subtype: p.subtype || "",
      unit: p.unit, price_xaf: p.price_xaf, image_url: p.image_url || "",
      description: p.description || "", available: p.available, quick_buy: p.quick_buy,
    });
    setEditing(p);
  };

  const save = async () => {
    if (!draft.name || !draft.shop_id) { toast.error("Nom et boutique requis"); return; }
    setLoading(true);
    try {
      const payload = { ...draft, price_xaf: parseInt(draft.price_xaf, 10) || 0 };
      if (editing === "new") { await grocery.adminCreateProduct(payload); toast.success("Produit créé"); }
      else { await grocery.adminUpdateProduct(editing.id, payload); toast.success("Mis à jour"); }
      setEditing(null);
      const fresh = await grocery.products({ shop_id: shopId, limit: 500 });
      setProducts(fresh);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const del = async (p) => {
    if (!confirm(`Supprimer "${p.name}" ?`)) return;
    try {
      await grocery.adminDeleteProduct(p.id);
      toast.success("Supprimé");
      const fresh = await grocery.products({ shop_id: shopId, limit: 500 });
      setProducts(fresh);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <Label className="text-sm font-semibold">Boutique :</Label>
          <select
            value={shopId}
            onChange={(e) => setShopId(e.target.value)}
            className="bg-white border border-brand-line rounded-xl px-3 h-10 text-sm"
            data-testid="grocery-admin-product-shop-select"
          >
            {shops.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <span className="text-xs text-brand-inkSoft">{products.length} produits</span>
        </div>
        <Button onClick={startNew} disabled={!shopId} className="bg-brand-gold hover:bg-brand-goldDark text-brand-black" data-testid="grocery-admin-product-new">
          <Plus className="w-4 h-4 mr-1.5" /> Nouveau produit
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-sandlight text-brand-inkSoft uppercase text-[10px] tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">Nom</th>
              <th className="px-3 py-2 text-left hidden sm:table-cell">Cat.</th>
              <th className="px-3 py-2 text-right">Prix</th>
              <th className="px-3 py-2 text-center hidden md:table-cell">Quick</th>
              <th className="px-3 py-2 text-center">Disp.</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => (
              <tr key={p.id} className="border-b border-brand-line" data-testid={`grocery-admin-product-${p.id}`}>
                <td className="px-3 py-2 font-semibold text-brand-ink">{p.name}<div className="text-[11px] text-brand-inkSoft font-normal">/ {p.unit}</div></td>
                <td className="px-3 py-2 hidden sm:table-cell">{p.category}</td>
                <td className="px-3 py-2 text-right font-mono text-brand-goldDark">{formatXAF(p.price_xaf)}</td>
                <td className="px-3 py-2 text-center hidden md:table-cell">{p.quick_buy ? "⚡" : "—"}</td>
                <td className="px-3 py-2 text-center">{p.available ? "✅" : "🚫"}</td>
                <td className="px-3 py-2">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => startEdit(p)} data-testid={`grocery-admin-product-edit-${p.id}`}>
                      <Edit3 className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 w-8 p-0 text-brand-terracotta border-brand-terracotta/40" onClick={() => del(p)} data-testid={`grocery-admin-product-del-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <ModalForm title={editing === "new" ? "Nouveau produit" : "Modifier produit"} onClose={() => setEditing(null)} onSave={save} loading={loading} testid="grocery-admin-product-form">
          <Field label="Nom *" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} testid="prod-form-name" />
          <SelectField
            label="Catégorie *"
            value={draft.category}
            onChange={(v) => setDraft({ ...draft, category: v })}
            options={[
              { value: "meat", label: "Boucherie" }, { value: "fish", label: "Poissonnerie" },
              { value: "chicken", label: "Volaille" }, { value: "produce", label: "Fruits & Légumes" },
              { value: "staples", label: "Riz / Pâtes" }, { value: "drinks", label: "Boissons" },
              { value: "dry", label: "Épicerie sèche" }, { value: "hygiene", label: "Hygiène" },
              { value: "bakery", label: "Boulangerie" },
            ]}
            testid="prod-form-cat"
          />
          <Field label="Sous-type" value={draft.subtype} onChange={(v) => setDraft({ ...draft, subtype: v })} testid="prod-form-subtype" />
          <Field label="Unité" value={draft.unit} onChange={(v) => setDraft({ ...draft, unit: v })} testid="prod-form-unit" />
          <Field label="Prix (FCFA) *" type="number" value={draft.price_xaf} onChange={(v) => setDraft({ ...draft, price_xaf: v })} testid="prod-form-price" />
          <Field label="Image URL" value={draft.image_url} onChange={(v) => setDraft({ ...draft, image_url: v })} testid="prod-form-image" />
          <Field label="Description" value={draft.description} onChange={(v) => setDraft({ ...draft, description: v })} multi testid="prod-form-desc" />
          <div className="flex items-center justify-between">
            <Label className="text-sm">Disponible</Label>
            <Switch checked={draft.available} onCheckedChange={(v) => setDraft({ ...draft, available: v })} data-testid="prod-form-avail" />
          </div>
          <div className="flex items-center justify-between">
            <Label className="text-sm">⚡ Achat rapide (homepage)</Label>
            <Switch checked={draft.quick_buy} onCheckedChange={(v) => setDraft({ ...draft, quick_buy: v })} data-testid="prod-form-quick" />
          </div>
        </ModalForm>
      )}
    </div>
  );
}

// ===== ZONES =====
function ZonesTab() {
  const [zones, setZones] = useState([]);
  const [edits, setEdits] = useState({});

  const load = async () => {
    const z = await grocery.zones();
    setZones(z.zones || []);
    setEdits({});
  };
  useEffect(() => { load(); }, []);

  const save = async (z) => {
    const fee = edits[z.slug] ?? z.delivery_fee;
    try {
      await grocery.adminUpdateZone(z.slug, { delivery_fee: parseInt(fee, 10), name: z.name, priority: z.priority });
      toast.success(`Zone ${z.name} mise à jour`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-2">
      <p className="text-sm text-brand-inkSoft mb-3">
        Frais de livraison par zone N'Djamena. L'option Express ajoute +500 FCFA en sus.
      </p>
      {zones.map((z) => (
        <Card key={z.slug} className="p-4 rounded-2xl border-brand-line flex items-center gap-3 flex-wrap" data-testid={`grocery-admin-zone-${z.slug}`}>
          <div className="flex-1 min-w-[160px]">
            <div className="font-semibold text-brand-ink">{z.name}</div>
            <div className="text-[11px] uppercase tracking-wider text-brand-inkSoft">{z.slug} · priorité {z.priority}</div>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={edits[z.slug] ?? z.delivery_fee}
              onChange={(e) => setEdits({ ...edits, [z.slug]: e.target.value })}
              className="w-28 h-10"
              data-testid={`grocery-admin-zone-fee-${z.slug}`}
            />
            <span className="text-xs text-brand-inkSoft">FCFA</span>
            <Button size="sm" onClick={() => save(z)} className="bg-brand-gold hover:bg-brand-goldDark text-brand-black" data-testid={`grocery-admin-zone-save-${z.slug}`}>
              <Save className="w-4 h-4" />
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ===== ORDERS =====
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try { setOrders(await grocery.adminOrders()); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-xl font-semibold text-brand-ink">{orders.length} commande{orders.length > 1 ? "s" : ""}</h2>
        <Button size="sm" variant="outline" onClick={load} data-testid="grocery-admin-orders-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </Button>
      </div>
      <div className="space-y-2">
        {orders.length === 0 && <p className="text-sm text-brand-inkSoft">Aucune commande pour le moment.</p>}
        {orders.map((o) => (
          <Card key={o.id} className="p-4 rounded-2xl border-brand-line" data-testid={`grocery-admin-order-${o.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading font-semibold text-brand-ink">#{o.id.slice(-6).toUpperCase()}</span>
                  <Badge className={o.paid ? "bg-emerald-600" : "bg-amber-500"}>{o.paid ? "Payée" : "À régler"}</Badge>
                  <Badge variant="outline">{o.status}</Badge>
                  {o.express && <Badge className="bg-brand-terracotta">⚡ Express</Badge>}
                </div>
                <div className="text-xs text-brand-inkSoft mt-1">
                  {o.delivery_zone} · {o.delivery_address} · {o.customer_phone}
                </div>
                <div className="text-xs text-brand-inkSoft mt-0.5">
                  {(o.items || []).length} articles · {o.payment_method}
                </div>
              </div>
              <div className="text-right">
                <div className="font-heading font-bold text-brand-goldDark text-lg">{formatXAF(o.total_xaf)}</div>
                <div className="text-[11px] text-brand-inkSoft">
                  {o.created_at ? new Date(o.created_at).toLocaleString("fr-FR") : ""}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ===== Reusable form bits =====
function ModalForm({ title, children, onSave, onClose, loading, testid }) {
  return (
    <div className="fixed inset-0 z-[55] flex items-end sm:items-center justify-center bg-black/55 backdrop-blur-sm p-3" data-testid={testid}>
      <Card className="w-full max-w-lg rounded-3xl p-5 bg-white max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading text-lg font-semibold text-brand-ink">{title}</h3>
          <Button size="sm" variant="ghost" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <div className="space-y-3 overflow-y-auto flex-1">{children}</div>
        <div className="flex gap-2 mt-4 pt-3 border-t border-brand-line">
          <Button variant="outline" onClick={onClose} className="flex-1">Annuler</Button>
          <Button onClick={onSave} disabled={loading} className="flex-1 bg-brand-gold hover:bg-brand-goldDark text-brand-black" data-testid={`${testid}-save`}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
            Enregistrer
          </Button>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", multi = false, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-brand-inkSoft mb-1 block">{label}</Label>
      {multi ? (
        <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} data-testid={testid} />
      ) : (
        <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="h-11" data-testid={testid} />
      )}
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-brand-inkSoft mb-1 block">{label}</Label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-brand-line rounded-xl px-3 h-11 text-sm"
        data-testid={testid}
      >
        <option value="">— Choisir —</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
